import { DownloadIcon, InboxIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import { ApiError, errorMessage } from '@/api/client'
import { downloadFeedbackCsv } from '@/api/endpoints'
import {
  useMe,
  useStaffFeedback,
  useStaffFeedbackItem,
  useTriageFeedback,
} from '@/api/queries'
import type { FeedbackTriageState, StaffFeedbackItem } from '@/api/types'
import { PageBody, PageHeader } from '@/components/AppShell'
import { EmptyState, ErrorState, TableSkeleton } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { formatAbsolute, formatBytes } from '@/lib/format'
import {
  FEEDBACK_CATEGORIES,
  NO_FILTERS,
  TRIAGE_STATES,
  toApiFilters,
  type InboxFilters,
} from '@/pages/feedback'

const SELECT_CLASS =
  'flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs'

function stateBadge(state: FeedbackTriageState) {
  switch (state) {
    case 'new':
      return <Badge variant="default">new</Badge>
    case 'triaged':
      return <Badge variant="outline">triaged</Badge>
    case 'actioned':
      return <Badge variant="success">actioned</Badge>
    case 'dismissed':
      return <Badge variant="secondary">dismissed</Badge>
  }
}

function dash(value: string | null | undefined): string {
  return value === null || value === undefined || value === '' ? '—' : value
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="truncate font-mono text-xs" title={typeof children === 'string' ? children : undefined}>
        {children}
      </dd>
    </div>
  )
}

/**
 * The full report. The body is caller-supplied text and is rendered AS TEXT inside a `<pre>`:
 * React escapes it, so nothing the sender wrote can become markup. The app ships no markdown
 * renderer and no sanitiser, and adding one for this page would add an XSS surface to buy
 * formatting nobody triaging a report needs.
 */
function FeedbackDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const item = useStaffFeedbackItem(id)
  const triage = useTriageFeedback()
  const [note, setNote] = useState<string | undefined>(undefined)
  const data: StaffFeedbackItem | undefined = item.data
  const noteValue = note ?? data?.staff_note ?? ''

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent className="max-h-[90dvh] overflow-hidden sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>{data === undefined ? 'Feedback' : (data.title ?? '(no title)')}</DialogTitle>
          <DialogDescription>
            {data === undefined
              ? 'Loading…'
              : `${data.category} via ${data.source}, ${formatAbsolute(data.submitted_at)}`}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto py-4">
          {item.isError ? (
            <ErrorState error={item.error} title="Could not load this report" />
          ) : data === undefined ? (
            <TableSkeleton rows={4} columns={2} />
          ) : (
            <>
              <pre
                data-testid="feedback-body"
                className="max-h-72 overflow-auto rounded-md border border-border bg-muted/30 p-3 font-sans text-sm whitespace-pre-wrap"
              >
                {data.body ?? ''}
              </pre>
              {data.redactions > 0 ? (
                <p className="text-xs text-muted-foreground">
                  {data.redactions} secret-like value(s) were redacted before storage.
                </p>
              ) : null}

              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3">
                <Field label="User">{dash(data.user_email)}</Field>
                <Field label="Account">{dash(data.account_name)}</Field>
                <Field label="Zone">{dash(data.zone_name ?? data.zone_id)}</Field>
                <Field label="Identity">{dash(data.identity_name)}</Field>
                <Field label="Client">
                  {dash([data.client_name, data.client_version].filter(Boolean).join(' '))}
                </Field>
                <Field label="Protocol">{dash(data.protocol_version)}</Field>
                <Field label="Server">{dash(data.server_version)}</Field>
                <Field label="Helper">{dash(data.helper_version)}</Field>
              </dl>

              {data.counters === null ? null : (
                <div className="space-y-1">
                  <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                    Counters
                  </p>
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-3">
                    {Object.entries(data.counters).map(([key, value]) => (
                      <Field key={key} label={key}>
                        {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                      </Field>
                    ))}
                  </dl>
                </div>
              )}

              {data.attachment === null ? null : (
                <p className="text-xs text-muted-foreground" data-testid="feedback-attachment">
                  Attachment: <span className="font-mono">{data.attachment.filename}</span> (
                  {formatBytes(data.attachment.size_bytes)}, handle{' '}
                  <span className="font-mono">{data.attachment.handle}</span>). Its bytes are not
                  available from the console.
                </p>
              )}

              <div className="space-y-1.5">
                <p className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                  State
                </p>
                <div className="flex flex-wrap items-center gap-2">
                  {TRIAGE_STATES.map((state) => (
                    <Button
                      key={state}
                      size="sm"
                      variant={data.triage_state === state ? 'default' : 'outline'}
                      aria-pressed={data.triage_state === state}
                      disabled={triage.isPending}
                      onClick={() => {
                        triage.mutate({ id, body: { triage_state: state } })
                      }}
                    >
                      {state}
                    </Button>
                  ))}
                </div>
                {data.triaged_by === null ? null : (
                  <p className="text-xs text-muted-foreground">
                    Last set by {data.triaged_by}
                    {data.triaged_at === null ? '' : ` on ${formatAbsolute(data.triaged_at)}`}.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="staff-note">Staff note</Label>
                <Textarea
                  id="staff-note"
                  rows={3}
                  value={noteValue}
                  onChange={(event) => {
                    setNote(event.target.value)
                  }}
                />
              </div>

              {triage.isError ? (
                <Alert variant="destructive">
                  <AlertTitle>Could not save</AlertTitle>
                  <AlertDescription>{errorMessage(triage.error)}</AlertDescription>
                </Alert>
              ) : null}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            disabled={
              data === undefined || triage.isPending || noteValue === (data.staff_note ?? '')
            }
            onClick={() => {
              triage.mutate({ id, body: { staff_note: noteValue } }, { onSuccess: () => setNote(undefined) })
            }}
          >
            {triage.isPending ? 'Saving…' : 'Save note'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function Inbox() {
  const [filters, setFilters] = useState<InboxFilters>(NO_FILTERS)
  const [applied, setApplied] = useState<InboxFilters>(NO_FILTERS)
  const [openId, setOpenId] = useState<string | undefined>(undefined)
  const [exporting, setExporting] = useState(false)
  const [exportError, setExportError] = useState<string | undefined>(undefined)

  const apiFilters = useMemo(() => toApiFilters(applied), [applied])
  const list = useStaffFeedback(apiFilters, true)
  const rows = useMemo(() => list.data?.pages.flatMap((page) => page.items) ?? [], [list.data])

  function set<K extends keyof InboxFilters>(key: K, value: InboxFilters[K]) {
    setFilters((current) => ({ ...current, [key]: value }))
  }

  async function exportCsv() {
    setExporting(true)
    setExportError(undefined)
    try {
      const { blob, filename } = await downloadFeedbackCsv(apiFilters)
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename ?? 'saltseed-feedback.csv'
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(url)
    } catch (error) {
      setExportError(errorMessage(error))
    } finally {
      setExporting(false)
    }
  }

  return (
    <>
      <PageHeader
        eyebrow="Staff"
        title="Feedback inbox"
        description="Reports sent to saltseed staff by agents and from the console, across every account."
        actions={
          <Button
            variant="outline"
            disabled={exporting}
            onClick={() => {
              void exportCsv()
            }}
          >
            <DownloadIcon className="size-4" />
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
      />
      <PageBody>
        {exportError === undefined ? null : (
          <Alert variant="destructive">
            <AlertTitle>Could not export</AlertTitle>
            <AlertDescription>{exportError}</AlertDescription>
          </Alert>
        )}

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <InboxIcon className="size-4 text-primary" />
              Reports
            </CardTitle>
            <CardDescription>
              Newest first. The CSV export uses the filters currently applied.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <form
              className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4"
              onSubmit={(event) => {
                event.preventDefault()
                setApplied(filters)
              }}
            >
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-category">Category</Label>
                <select
                  id="f-category"
                  className={SELECT_CLASS}
                  value={filters.category}
                  onChange={(event) => {
                    set('category', event.target.value)
                  }}
                >
                  <option value="">All</option>
                  {FEEDBACK_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-state">State</Label>
                <select
                  id="f-state"
                  className={SELECT_CLASS}
                  value={filters.state}
                  onChange={(event) => {
                    set('state', event.target.value)
                  }}
                >
                  <option value="">All</option>
                  {TRIAGE_STATES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-zone">Zone id</Label>
                <Input
                  id="f-zone"
                  className="font-mono"
                  value={filters.zone}
                  onChange={(event) => {
                    set('zone', event.target.value)
                  }}
                />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-client">Client</Label>
                <Input
                  id="f-client"
                  value={filters.client}
                  onChange={(event) => {
                    set('client', event.target.value)
                  }}
                />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-from">From</Label>
                <Input
                  id="f-from"
                  type="date"
                  value={filters.from}
                  onChange={(event) => {
                    set('from', event.target.value)
                  }}
                />
              </div>
              <div className="min-w-0 space-y-1.5">
                <Label htmlFor="f-to">To</Label>
                <Input
                  id="f-to"
                  type="date"
                  value={filters.to}
                  onChange={(event) => {
                    set('to', event.target.value)
                  }}
                />
              </div>
              <div className="min-w-0 space-y-1.5 sm:col-span-2 lg:col-span-1">
                <Label htmlFor="f-q">Search</Label>
                <Input
                  id="f-q"
                  value={filters.q}
                  onChange={(event) => {
                    set('q', event.target.value)
                  }}
                />
              </div>
              <div className="flex items-end gap-2">
                <Button type="submit">Apply</Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilters(NO_FILTERS)
                    setApplied(NO_FILTERS)
                  }}
                >
                  Clear
                </Button>
              </div>
            </form>

            {list.isError ? (
              <ErrorState
                error={list.error}
                title="Could not load the inbox"
                onRetry={() => {
                  void list.refetch()
                }}
              />
            ) : list.isPending ? (
              <TableSkeleton rows={5} columns={8} />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={InboxIcon}
                title="No feedback matches"
                description="Nothing has been sent yet, or the filters exclude it."
              />
            ) : (
              <>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Submitted</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Title</TableHead>
                        <TableHead>Source</TableHead>
                        <TableHead>Zone</TableHead>
                        <TableHead>Identity</TableHead>
                        <TableHead>Client</TableHead>
                        <TableHead>Protocol</TableHead>
                        <TableHead>State</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rows.map((row) => (
                        <TableRow
                          key={row.feedback_id}
                          data-testid="feedback-row"
                          className="cursor-pointer"
                          onClick={() => {
                            setOpenId(row.feedback_id)
                          }}
                        >
                          <TableCell className="text-xs text-muted-foreground tabular">
                            {formatAbsolute(row.submitted_at)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{row.category}</Badge>
                          </TableCell>
                          <TableCell className="max-w-[18rem]">
                            <button
                              type="button"
                              className="block max-w-full truncate text-left text-sm underline-offset-2 hover:underline"
                              title={row.title ?? undefined}
                              onClick={(event) => {
                                event.stopPropagation()
                                setOpenId(row.feedback_id)
                              }}
                            >
                              {row.title ?? '(no title)'}
                            </button>
                          </TableCell>
                          <TableCell className="text-xs">{row.source}</TableCell>
                          <TableCell className="max-w-[10rem] truncate font-mono text-xs">
                            {dash(row.zone_name)}
                          </TableCell>
                          <TableCell className="max-w-[10rem] truncate font-mono text-xs">
                            {dash(row.identity_name)}
                          </TableCell>
                          <TableCell className="text-xs">
                            {dash([row.client_name, row.client_version].filter(Boolean).join(' '))}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {dash(row.protocol_version)}
                          </TableCell>
                          <TableCell>{stateBadge(row.triage_state)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                <div className="flex items-center gap-3">
                  <p className="text-xs text-muted-foreground">{rows.length} shown</p>
                  {list.hasNextPage ? (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={list.isFetchingNextPage}
                      onClick={() => {
                        void list.fetchNextPage()
                      }}
                    >
                      {list.isFetchingNextPage ? 'Loading…' : 'Load more'}
                    </Button>
                  ) : null}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </PageBody>

      {openId === undefined ? null : (
        <FeedbackDetail
          id={openId}
          onClose={() => {
            setOpenId(undefined)
          }}
        />
      )}
    </>
  )
}

/** Staff-only. A non-staff user who types the URL gets the refusal, not an empty inbox. */
export function StaffFeedbackPage() {
  const me = useMe()
  if (me.isPending) return <TableSkeleton rows={3} columns={3} />
  if (me.isError || me.data.staff !== true) {
    const couldNotCheck = me.isError && !(me.error instanceof ApiError && me.error.isForbidden)
    return (
      <>
        <PageHeader eyebrow="Staff" title="Not available" />
        <PageBody>
          <Alert variant="destructive" data-testid="staff-forbidden">
            <AlertTitle>403 — this page is for saltseed staff</AlertTitle>
            <AlertDescription>
              {couldNotCheck
                ? 'Could not check your access. Try again shortly.'
                : 'Your account does not have the staff role.'}
            </AlertDescription>
          </Alert>
        </PageBody>
      </>
    )
  }
  return <Inbox />
}
